import { WinChallengeVerwaltung } from './WinChallengeVerwaltung';
import { PollCountdown } from '../components/PollCountdown';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { OwnerState, PollFrage } from '../lib/types';

/**
 * Challenges im Regiepult.
 *
 * Zwei Dinge an einem Ort: der Vorrat an Fragen, und die eine Abstimmung, die gerade läuft.
 *
 * Die laufende Abstimmung steht oben, weil sie der einzige Teil ist, der während des Streams
 * angefasst wird – alles darunter wird vorher einmal eingerichtet.
 */
export function Challenges({ state }: { state: OwnerState }) {
  const [fragen, setFragen] = useState<PollFrage[] | null>(null);
  const [nextAt, setNextAt] = useState<string | null>(null);
  const [automaticEnabled, setAutomaticEnabled] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);
  const [neueFrage, setNeueFrage] = useState(false);
  const [minimiert, setMinimiert] = useState(false);
  const [bearbeiten, setBearbeiten] = useState<PollFrage | null>(null);
  const [settings, setSettings] = useState({
    firstDelayMinutes: 10, repeatMinutes: 15, cutoffMinutes: 10, nextAt: null as string | null,
    announcement: 'Eine neue Abstimmung wurde gestartet. Stimme jetzt ab, welche Aufgabe als nächstes erledigt werden muss!',
  });
  useEffect(() => {
    void api.pollSettings(state.lobby.id).then((value) => { setSettings(value); setNextAt(value.nextAt); setAutomaticEnabled(value.automaticEnabled); }).catch(() =>
      setMeldung('Automatische Einstellungen konnten nicht geladen werden.'));
  }, [state.lobby.id]);

  useEffect(() => {
    const id = setInterval(() => void api.pollSettings(state.lobby.id)
      .then((value) => {
        setNextAt(value.nextAt);
        setAutomaticEnabled(value.automaticEnabled);
      }).catch(() => {}), 5000);
    return () => clearInterval(id);
  }, [state.lobby.id]);

  const laden = () => {
    void api
      .questions(state.lobby.id)
      .then(setFragen)
      .catch(() => setFragen([]));
  };
  useEffect(laden, [state.lobby.id]);

  async function tun(was: () => Promise<unknown>, erfolg: string) {
    setLäuft(true);
    setMeldung(null);
    try {
      await was();
      setMeldung(erfolg);
      laden();
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setLäuft(false);
    }
  }

  const poll = state.poll;

  return (
    <section className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2>Challenge-Umfragen</h2>
        <button type="button" className="ghost" onClick={() => setMinimiert(!minimiert)}>
          {minimiert ? 'Ausklappen' : 'Minimieren'}
        </button>
      </div>

      {!minimiert && !state.lobby.challengesEnabled && (
        <p className="notice calm">
          Challenges sind für diese Lobby abgeschaltet. Du kannst sie hier trotzdem vorbereiten –
          einschalten lässt sich das unter „Einrichten → Spiel“.
        </p>
      )}

      {!minimiert && <div className="panel" style={{ marginBottom: 18 }}>
        <h3>Automatische Abstimmungen</h3>
        <p className="muted small">
          Umfrage-Timer: {automaticEnabled ? 'Aktiv' : 'Ausgeschaltet'}
        </p>
        <div className="row">
          <button type="button" disabled={läuft || !automaticEnabled}
            onClick={() => void tun(async () => {
              const result = await api.setAutomaticPolls(state.lobby.id, false);
              setAutomaticEnabled(result.automaticEnabled);
              setNextAt(result.nextAt);
            }, 'Umfrage-Timer ausgeschaltet.')}>
            Challenge-Umfragen beenden
          </button>
          <button type="button" disabled={läuft || automaticEnabled}
            onClick={() => void tun(async () => {
              const result = await api.setAutomaticPolls(state.lobby.id, true);
              setAutomaticEnabled(result.automaticEnabled);
              setNextAt(result.nextAt);
            }, 'Umfrage-Timer gestartet.')}>
            Challenge-Umfragen starten
          </button>
        </div>
        <label>Erste Abstimmung nach (Minuten)
          <input type="number" min={0} max={1440} value={settings.firstDelayMinutes}
            onChange={(e) => setSettings({ ...settings, firstDelayMinutes: Number(e.target.value) })} />
        </label>
        <label>Wiederholungsintervall nach Abstimmungsende (Minuten)
          <input type="number" min={1} max={1440} value={settings.repeatMinutes}
            onChange={(e) => setSettings({ ...settings, repeatMinutes: Number(e.target.value) })} />
        </label>
        <label>Chatnachricht beim Abstimmungsstart
          <textarea maxLength={450} rows={3} value={settings.announcement}
            onChange={(e) => setSettings({ ...settings, announcement: e.target.value })} />
        </label>
        <button type="button" disabled={läuft} onClick={() =>
          void tun(() => api.savePollSettings(state.lobby.id, settings), 'Einstellungen gespeichert.')
        }>Einstellungen speichern</button>
        <p className="muted small">{!automaticEnabled ? 'Umfrage-Timer ausgeschaltet' : nextAt ? <>Nächste Abstimmung in <PollCountdown closesAt={nextAt} /></> : 'Die nächste Abstimmung wird nach dem Start der Challenge-Automatik oder dem Ende der aktuellen Abstimmung geplant.'}</p>
      </div>}

      {poll && (
        <div className={poll.offen ? 'poll-stand offen' : 'poll-stand'}>
          <div className="poll-kopf">
            <strong>{poll.question}</strong>
            {poll.offen && <PollCountdown closesAt={poll.closesAt} />}
          </div>

          <ul className="poll-antworten klein">
            {poll.antworten.map((antwort) => (
              <li key={antwort.id}>
                <div
                  className="poll-balken"
                  style={{ width: `${Math.round(antwort.anteil * 100)}%` }}
                />
                <span className="poll-nummer">{antwort.nummer}</span>
                <span className="poll-label">{antwort.label}</span>
                <span className="poll-stimmen">{antwort.stimmen}</span>
              </li>
            ))}
          </ul>

          {poll.offen ? (
            <div className="row">
              <button
                type="button"
                disabled={läuft}
                onClick={() => void tun(() => api.closePoll(state.lobby.id), 'Abstimmung beendet.')}
              >
                Jetzt schließen
              </button>
              <button
                type="button"
                className="ghost gefahr"
                disabled={läuft}
                onClick={() =>
                  void tun(() => api.cancelPoll(state.lobby.id), 'Abstimmung abgebrochen.')
                }
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <Entschieden state={state} />
          )}
        </div>
      )}

      {!minimiert && <ul className="zeilen">
        {(fragen ?? []).map((frage) => (
          <li key={frage.id} className={frage.enabled ? 'zeile' : 'zeile inaktiv'}>
            <span className="zeile-text">
              {frage.question}
              <span className="muted small"> · {frage.answers.length} Antworten</span>
            </span>
            <button type="button" className="ghost" disabled={läuft || (poll?.offen && poll.question === frage.question)}
              onClick={() => { setNeueFrage(false); setBearbeiten(frage); }}>
              Bearbeiten
            </button>
            <button
              type="button"
              className="primary"
              disabled={läuft || !!poll?.offen}
              title={poll?.offen ? 'Es läuft bereits eine Abstimmung.' : undefined}
              onClick={() =>
                void tun(() => api.openPoll(state.lobby.id, frage.id), 'Abstimmung läuft.')
              }
            >
              Abstimmen lassen
            </button>
            <button
              type="button"
              className="ghost gefahr"
              disabled={läuft}
              onClick={() =>
                void tun(() => api.removeQuestion(state.lobby.id, frage.id), 'Gelöscht.')
              }
            >
              Löschen
            </button>
          </li>
        ))}
        {fragen && fragen.length === 0 && (
          <li className="muted small">Noch keine Frage vorbereitet.</li>
        )}
      </ul>}

      {!minimiert && (neueFrage || bearbeiten ? (
        <NeueFrage
          key={bearbeiten?.id ?? 'neu'}
          lobbyId={state.lobby.id}
          bestehend={bearbeiten}
          onFertig={() => { setNeueFrage(false); setBearbeiten(null); laden(); }}
          onAbbruch={() => { setNeueFrage(false); setBearbeiten(null); }}
        />
      ) : (
        <button type="button" onClick={() => { setBearbeiten(null); setNeueFrage(true); }}>
          Frage vorbereiten
        </button>
      ))}

      {!minimiert && <WinChallengeVerwaltung lobbyId={state.lobby.id} />}
      {meldung && <p className="muted small">{meldung}</p>}
    </section>
  );
}

/** Was nach dem Schließen zu tun ist – im gemeinsamen Modus eines, je Kanal mehrere. */
function Entschieden({ state }: { state: OwnerState }) {
  const poll = state.poll!;

  if (poll.mode === 'PER_CHANNEL' && poll.jeKanal) {
    return (
      <ul className="zeilen">
        {poll.jeKanal.map((eintrag) => {
          const team = state.teams.find((t) => t.id === eintrag.teamId);
          return (
            <li key={eintrag.channelId} className="zeile">
              <span className="punkt" style={{ background: team?.color ?? '#555' }} />
              <span className="zeile-text">
                {eintrag.gewinner?.challenge ?? (
                  <span className="muted">
                    {eintrag.gleichstand ? 'Gleichstand' : 'keine Stimme'}
                  </span>
                )}
              </span>
              <span className="muted small">{team ? `Team: ${team.name}` : 'ohne Team'}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  if (poll.ergebnis?.gleichstand) {
    return (
      <p className="notice">
        Gleichstand zwischen {poll.ergebnis.führende.map((a) => a.label).join(' und ')}. Entscheide
        du – oder lass noch einmal abstimmen.
      </p>
    );
  }

  if (!poll.ergebnis?.gewinner) {
    return <p className="muted small">Niemand hat abgestimmt.</p>;
  }

  return (
    <p className="challenge-ergebnis">
      <strong>{poll.ergebnis.gewinner.challenge}</strong>
      <span className="muted small">
        {' '}
        · {poll.ergebnis.gewinner.label} · {Math.round(poll.ergebnis.gewinner.challengeSeconds / 60)}{' '}
        Minuten
      </span>
    </p>
  );
}

/**
 * Eine neue Frage mit ihren Antworten.
 *
 * Frage und Antworten entstehen in einem Zug, weil eine Frage ohne Antworten nichts ist, womit
 * sich abstimmen ließe – sie würde nur als halbe Zeile in der Liste stehen.
 */
function NeueFrage({
  lobbyId,
  bestehend,
  onFertig,
  onAbbruch,
}: {
  lobbyId: string;
  bestehend: PollFrage | null;
  onFertig: () => void;
  onAbbruch: () => void;
}) {
  const [frage, setFrage] = useState(bestehend?.question ?? '');
  const [sekunden, setSekunden] = useState(bestehend?.voteSeconds ?? 180);
  const [antworten, setAntworten] = useState(
    bestehend ? bestehend.answers.map((a) => ({ label: a.label, challenge: a.challenge, minuten: a.challengeSeconds / 60 })) : [
      { label: '', challenge: '', minuten: 5 },
      { label: '', challenge: '', minuten: 5 },
    ]
  );
  const [fehler, setFehler] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);

  const ändere = (i: number, feld: 'label' | 'challenge' | 'minuten', wert: string | number) =>
    setAntworten((bisher) => bisher.map((a, j) => (i === j ? { ...a, [feld]: wert } : a)));

  const vollständig =
    frage.trim() && antworten.every((a) => a.label.trim() && a.challenge.trim());

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLäuft(true);
    try {
      const daten = {
        question: frage,
        voteSeconds: sekunden,
        answers: antworten.map((a) => ({
          label: a.label,
          challenge: a.challenge,
          challengeSeconds: a.minuten * 60,
        })),
      };
      if (bestehend) await api.updateQuestion(lobbyId, bestehend.id, daten);
      else await api.addQuestion(lobbyId, daten);
      onFertig();
    } catch (err) {
      setFehler(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
      setLäuft(false);
    }
  }

  return (
    <form className="stack panel" onSubmit={(e) => void speichern(e)}>
      <h3>{bestehend ? 'Challenge-Umfrage bearbeiten' : 'Neue Challenge-Umfrage'}</h3>
      <label>
        Frage an den Chat
        <input
          value={frage}
          maxLength={200}
          autoFocus
          placeholder="z. B. Was muss als Nächstes passieren?"
          onChange={(e) => setFrage(e.target.value)}
        />
      </label>

      <label>
        Wie lange abgestimmt wird (Sekunden)
        <input
          type="number"
          min={15}
          max={3600}
          value={sekunden}
          onChange={(e) => setSekunden(Number(e.target.value))}
        />
      </label>

      <p className="muted small">
        Zu jeder Antwort gehört eine Challenge. Was gewinnt, müsst ihr tun. Die Nummern im Chat
        werden bei jeder Abstimmung neu gemischt – sonst stimmt ein Stammpublikum irgendwann ab,
        ohne die Frage zu lesen.
      </p>

      {antworten.map((antwort, i) => (
        <div key={i} className="antwort-block">
          <div className="feld-paar">
            <label>
              Antwort {i + 1}
              <input
                value={antwort.label}
                maxLength={80}
                onChange={(e) => ändere(i, 'label', e.target.value)}
              />
            </label>
            <label>
              Dauer der Challenge (Minuten)
              <input
                type="number"
                min={1}
                max={120}
                value={antwort.minuten}
                onChange={(e) => ändere(i, 'minuten', Number(e.target.value))}
              />
            </label>
          </div>
          <label>
            Challenge
            <input
              value={antwort.challenge}
              maxLength={300}
              placeholder="Was zu tun ist, wenn diese Antwort gewinnt"
              onChange={(e) => ändere(i, 'challenge', e.target.value)}
            />
          </label>
          {antworten.length > 2 && (
            <button
              type="button"
              className="ghost gefahr"
              onClick={() => setAntworten((b) => b.filter((_, j) => j !== i))}
            >
              Antwort {i + 1} entfernen
            </button>
          )}
        </div>
      ))}

      {antworten.length < 10 && (
        <button
          type="button"
          className="ghost"
          onClick={() => setAntworten((b) => [...b, { label: '', challenge: '', minuten: 5 }])}
        >
          Antwort hinzufügen
        </button>
      )}

      {fehler && <p className="notice">{fehler}</p>}

      <div className="row">
        <button type="submit" className="primary" disabled={läuft || !vollständig}>
          Speichern
        </button>
        <button type="button" className="ghost" onClick={onAbbruch} disabled={läuft}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
